import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Icon,
  Input,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { MdOutlineEmail } from "react-icons/md";
import { API, graphqlOperation } from "aws-amplify";
import { updateGuestEmail } from "../../../Backend/Notifications/updateGuestEmail";
import { getGuest } from "../../../graphql/queries";

const EmailNotificationsModal = ({ isOpen, onClose, guestId, bookingId }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [emailValue, setEmailValue] = useState("");
  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      setEmailValue("");
      setErrorMessage("");
      setIsLoading(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !guestId) return;
    let cancelled = false;

    const loadGuest = async () => {
      try {
        const res = await API.graphql(
          graphqlOperation(getGuest, { id: guestId })
        );
        if (cancelled) return;
        const email = res?.data?.getGuest?.email;
        if (email && !email.toLowerCase().includes("@noemail.com")) {
          setEmailValue(email);
        }
      } catch (err) {
        console.error("Failed to fetch guest email:", err);
      }
    };

    loadGuest();

    return () => {
      cancelled = true;
    };
  }, [isOpen, guestId]);

  const isValidEmail = (value) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || "").trim());

  const handleUpdate = async () => {
    const trimmedEmail = emailValue.trim();

    if (!isValidEmail(trimmedEmail)) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }

    setIsLoading(true);
    setErrorMessage("");
    
    try {
      await updateGuestEmail(guestId, trimmedEmail, bookingId);
      
      toast({
        title: "Email updated",
        description: "We'll send updates to this email when your request is approved.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onClose();
    } catch (error) {
      console.error("Error updating email:", error);
      const errorMsg = error.response?.data?.message || error.message || "Failed to update email. Please try again.";
      setErrorMessage(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleIgnore = () => {
    // Save to session storage to prevent spamming
    sessionStorage.setItem("emailNotificationIgnored", "true");
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !isLoading) {
      e.preventDefault();
      handleUpdate();
    }
  };

  return (
    <Drawer placement="bottom" onClose={onClose} isOpen={isOpen}>
      <DrawerOverlay />
      <DrawerContent
        maxH="90vh"
        borderTopRadius="2xl"
        bg="gray.900"
        color="white"
        maxW={{ base: "100%", md: "560px" }}
        mx="auto"
        mb={{ md: 8 }}
        borderRadius={{ md: "24px" }}
      >
        <Flex
          mx={3}
          my={4}
          alignItems="center"
          justifyContent="space-between"
        >
          <Flex align="center" gap={3}>
            <Flex
              align="center"
              justify="center"
              boxSize="36px"
              borderRadius="full"
              bg="whiteAlpha.100"
              border="1px solid"
              borderColor="whiteAlpha.200"
            >
              <Icon as={MdOutlineEmail} boxSize={5} color="#817EFF" />
            </Flex>
            <Text fontSize="18px" fontWeight={800}>
              Want to stay informed?
            </Text>
          </Flex>
          <DrawerCloseButton pos="relative" top={0} right={0} />
        </Flex>

        <DrawerBody px={4} pt={0} pb={6}>
          <Text fontSize="13px" color="whiteAlpha.700" mb={4}>
            Add the best email for your trip. We'll use it to send updates when
            your request is approved or needs attention.
          </Text>

          <Box
            bg="whiteAlpha.50"
            border="1px solid"
            borderColor={errorMessage ? "red.400" : "whiteAlpha.200"}
            borderRadius="14px"
            p={3}
            transition="border-color 0.15s ease"
          >
            <Text
              fontSize="11px"
              color="whiteAlpha.600"
              letterSpacing="0.08em"
              textTransform="uppercase"
              fontWeight={700}
              mb={1}
            >
              Email address
            </Text>
            <Input
              variant="unstyled"
              value={emailValue}
              onChange={(e) => {
                setEmailValue(e.target.value);
                if (errorMessage) setErrorMessage("");
              }}
              onKeyDown={handleKeyDown}
              placeholder="you@example.com"
              color="white"
              fontSize="15px"
              fontWeight={600}
              type="email"
              autoFocus
              _placeholder={{ color: "whiteAlpha.500" }}
            />
          </Box>

          {errorMessage && (
            <Alert
              status="error"
              borderRadius="12px"
              mt={3}
              bg="red.900"
              color="red.100"
            >
              <AlertIcon color="red.300" />
              <Text fontSize="13px">{errorMessage}</Text>
            </Alert>
          )}

          <Stack spacing={2} mt={5}>
            <Button
              w="full"
              bg="#817EFF"
              color="white"
              h="48px"
              borderRadius="14px"
              fontSize="14px"
              fontWeight={700}
              leftIcon={<MdOutlineEmail />}
              _hover={{ bg: "#615edc" }}
              _active={{ bg: "#4b49c4" }}
              onClick={handleUpdate}
              isLoading={isLoading}
              loadingText="Updating..."
            >
              Update email
            </Button>
            <Button
              w="full"
              variant="ghost"
              color="whiteAlpha.800"
              h="44px"
              borderRadius="14px"
              fontSize="13px"
              fontWeight={600}
              _hover={{ bg: "whiteAlpha.100" }}
              _active={{ bg: "whiteAlpha.200" }}
              onClick={handleIgnore}
              isDisabled={isLoading}
            >
              Maybe later
            </Button>
          </Stack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default EmailNotificationsModal; 